import { useEffect, useRef, useState } from "react";
import { FolderUp, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/** Bulk personnel intake — many CVs/certificates at once, or a ZIP folder of them. */
export const PersonnelBulkUpload = ({ onDone }) => {
  const inputRef = useRef(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!job || job.status !== "processing") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/personnel/jobs/${job.job_id}`);
        setJob(data);
        if (data.status === "done") {
          clearInterval(t);
          toast.success(`Bulk intake finished — ${data.created} created, ${data.updated} updated${data.failed ? `, ${data.failed} failed` : ""}`);
          onDone?.();
        }
      } catch (err) {
        console.warn("bulk job polling stopped:", err);
        clearInterval(t);
      }
    }, 2500);
    return () => clearInterval(t);
  }, [job, onDone]);

  const send = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      [...files].forEach((f) => fd.append("files", f));
      const { data } = await api.post("/personnel/bulk", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setJob(data);
      toast.success(`Reading ${data.total} document(s) in the background`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white p-4" data-testid="personnel-bulk">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-heading font-bold text-sm text-[#111827]">Bulk personnel intake</p>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Drop in a ZIP folder of staff CVs, degree and council certificates, training or competency
            records — or select many files at once. Each document is read and matched to an existing person by
            employee ID or name, otherwise a new file is created. Nothing already on record is overwritten.
          </p>
        </div>
        <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={busy}
          data-testid="personnel-bulk-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FolderUp className="h-4 w-4 mr-1.5 text-[#002FA7]" />}
          Upload folder or files
        </Button>
        <input ref={inputRef} type="file" multiple className="hidden" data-testid="personnel-bulk-input"
          accept=".zip,.pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.heic,.heif"
          onChange={(e) => { send(e.target.files); e.target.value = ""; }} />
      </div>

      {job && (
        <div className="mt-3" data-testid="personnel-bulk-progress">
          <div className="flex justify-between text-xs text-[#6B7280]">
            <span>{job.status === "done" ? "Completed" : "Reading documents…"}</span>
            <span>{job.processed}/{job.total}</span>
          </div>
          <div className="h-2 rounded-full bg-[#F3F4F6] mt-1 overflow-hidden">
            <div className="h-full bg-[#002FA7] rounded-full transition-all"
              style={{ width: `${(job.processed / Math.max(job.total, 1)) * 100}%` }} />
          </div>
          <p className="text-xs text-[#111827] mt-1.5">
            {job.created} created · {job.updated} updated
            {job.failed ? <span className="text-[#C92A2A]"> · {job.failed} failed</span> : null}
          </p>
          {job.errors?.length > 0 && (
            <ul className="mt-1 space-y-0.5" data-testid="personnel-bulk-errors">
              {job.errors.slice(0, 4).map((e, i) => (
                <li key={i} className="text-[11px] text-[#C92A2A] truncate">{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
